// src/lib/sms-templates-api.js — Éditeur SMS : client admin de la vue SMS.
//
// Deux accès :
//   - LECTURE : select direct de la table `sms_templates` (RLS admin_read,
//     FOR SELECT seul). NON FATALE : table absente (migration pas encore
//     appliquée) ou erreur → map vide, la vue affiche les textes codés
//     (fallback naturel).
//   - ÉCRITURE : action serveur `sms_template_save` UNIQUEMENT (requireAdmin
//     + validation lib/sms-validator.js côté serveur). Une écriture directe
//     est IMPOSSIBLE par construction : la policy admin est SELECT-only —
//     le validateur (barrière légale {lien_stop} L.34-5) n'est pas
//     contournable depuis la console navigateur.

import { supabase } from "../supabase";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Non authentifié");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

// body_es (migration 20261027000001) peut ne pas exister encore : SELECT complet
// tenté d'abord, RETRY sans body_es si la colonne est absente (deploy avant
// apply). Erreur « colonne absente » = 42703 / PGRST204 / message citant body_es.
const SMS_TPL_COLUMNS = "slug, body_fr, body_en, body_es, updated_at";
const SMS_TPL_COLUMNS_LEGACY = "slug, body_fr, body_en, updated_at";
// Classification RESSERRÉE : codes définitifs (42703 / PGRST204) OU message
// citant body_es ET une formulation d'absence réelle — pas toute occurrence de
// « body_es » (une vraie erreur DB citant la colonne doit remonter, pas être
// avalée en fallback legacy silencieux).
const MISSING_COL_PHRASE_RE =
  /does not exist|could not find|schema cache|undefined column|unknown column/i;
function isMissingBodyEsError(error) {
  if (!error) return false;
  if (error.code === "42703" || error.code === "PGRST204") return true;
  const msg = String(error.message || "");
  return /body_es/.test(msg) && MISSING_COL_PHRASE_RE.test(msg);
}

/**
 * Corps édités/traduits des gabarits SMS :
 * { slug → {body_fr, body_en, body_es, updated_at} }.
 * L'admin n'édite QUE body_fr ; body_en/body_es sont GÉNÉRÉS au save (serveur)
 * et affichés en lecture seule. Fail-soft : toute erreur (table absente, RLS,
 * réseau) → {} (textes codés). body_es absent (colonne pas créée) → RETRY sans.
 */
export async function fetchSmsTemplatesMap() {
  try {
    let { data, error } = await supabase
      .from("sms_templates")
      .select(SMS_TPL_COLUMNS);
    if (error && isMissingBodyEsError(error)) {
      ({ data, error } = await supabase
        .from("sms_templates")
        .select(SMS_TPL_COLUMNS_LEGACY));
    }
    if (error) throw error;
    const map = {};
    for (const r of data || []) {
      if (r && typeof r.slug === "string") map[r.slug] = r;
    }
    return map;
  } catch (e) {
    // Fail-soft assumé : la vue reste utilisable en lecture (textes codés).
    console.error("[sms-templates] lecture sms_templates échouée", e);
    return {};
  }
}

/**
 * Sauvegarde d'un corps de gabarit SMS via l'action serveur (validée serveur).
 * L'admin n'envoie QUE body_fr : les traductions EN + ES sont générées serveur
 * (body_en/body_es d'un client seraient IGNORÉS). La réponse porte
 * `translations: { en:{ok,body?|reason}, es:{...} }` (échec = langue en fallback).
 * `regenerate:true` (bouton « Régénérer ») → traduction NON déterministe côté
 * serveur (variante) ; le save normal reste déterministe.
 * @param {{slug:string, op?:'set'|'reset', body_fr?:string, regenerate?:boolean}} payload
 * @returns {Promise<object>} { ok, op, slug, warnings?, worst_case?, translations? }
 * @throws {Error} avec `err.server` = payload d'erreur serveur (errors/warnings)
 */
export async function saveSmsTemplate(payload) {
  const res = await fetch("/api/beds24-mapping?action=sms_template_save", {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify({ action: "sms_template_save", ...payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || "Sauvegarde du gabarit SMS échouée");
    err.server = json;
    throw err;
  }
  return json;
}
